import { useEffect, useMemo, useRef, useState } from 'react';
import { getGamesData, getLinksData, runEvScreen, runEvScreenPaginated, runEvScreenPaginatedProgressive } from '../../../utils/api/evScreen';
import { getWagerWizDisplayOptions, DEFAULT_SPORTSBOOKS } from '../../../utils/constants';
import { pushSportsbooksToQueryField } from '../../../utils/sportsbookAliases';
import { delay } from '../../../utils/delay';
import { IOSSwitch } from '../../../components/IOSSwitch';
import './evScreen.css';
import filter_icon from '../../../assets/images/filter_icon.svg';
import play_icon from '../../../assets/images/play_icon.svg';
import { EVScreenTable } from '../EvScreenTable/evScreenTable';
import wwlogo_gif from '../../../assets/ww_loading2.gif';
import { FiltersContainer } from '../Filters/FiltersContainer';
import { getFutureDate } from '../../../utils/getFutureDate';
import Joyride from 'react-joyride';
import { parsedevScanResults, sampleRelatedOdds } from './sampleData';
import axios from 'axios';
import lightbulb_icon from '../../../assets/images/lightbulb_icon.svg';
import { Modal, Popover, Tooltip } from '@mui/material';
import eye_icon from '../../../assets/images/eye_icon.svg';
import edit_icon from '../../../assets/images/edit_icon.svg';
import { RenameQuery } from './RenameQuery';
import { EvScreenControlButtons } from './EvScreenControlButtons';
import { EvScreenControlButtonsMobile } from './EvScreenControlButtonsMobile';
import { Footer } from '../../../components/Footer/Footer';
import { useAuth0 } from '@auth0/auth0-react';
import { UpgradeModal } from '../../../components/UpgradeModal/UpgradeModal';
import { VimeoEmbed } from '../../landing/VideoEmbed';
import { CompactPaginationControls } from '../../../components/CompactPaginationControls/CompactPaginationControls';

const FREE_DAILY_SCAN_LIMIT = 8;
const SCAN_COUNT_KEY = 'ev_scan_count';

function getTodayDateStr() {
	return new Date().toISOString().slice(0, 10);
}

function getDailyScanCount() {
	try {
		const raw = localStorage.getItem(SCAN_COUNT_KEY);
		if (!raw) return 0;
		const parsed = JSON.parse(raw);
		if (parsed.date !== getTodayDateStr()) return 0;
		return parsed.count || 0;
	} catch {
		return 0;
	}
}

function incrementDailyScanCount() {
	if (!isUserOnFreePlan()) return;
	const today = getTodayDateStr();
	const current = getDailyScanCount();
	localStorage.setItem(SCAN_COUNT_KEY, JSON.stringify({ date: today, count: current + 1 }));
}

function isUserOnFreePlan() {
	try {
		const userdb = JSON.parse(localStorage.getItem('userdb'));
		if (!userdb) return true;
		return !userdb.stripe_subscription || userdb.stripe_status === 'deleted' || userdb.stripe_status === 'past_due';
	} catch {
		return true;
	}
}

function isDailyScanLimitReached() {
	if (!isUserOnFreePlan()) return false;
	return getDailyScanCount() >= FREE_DAILY_SCAN_LIMIT;
}

const tutorialSteps = [
	{
		target: '.ev-scanner-title',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Explore the EV scanner and other tools</h3>
				<div style={{ color: '#838693' }}>Find +EV betting opportunities by finding outliers in the market</div>
			</div>
		),
		placement: 'left',
		scrollOffset: 50,
		hideCloseButton: true,
		disableBeacon: true,
	},
	{
		target: '.filter-button-tutorial',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Set your Betting Filters</h3>
				<div style={{ color: '#838693' }}>Customize filters to match your needs - select sports, books, sharps, EV thresholds, and more.</div>
			</div>
		),
	},
	{
		target: '.leagues-dropdown-tutorial',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Customize your experience</h3>
				<div style={{ color: '#838693' }}>Use the dropdowns to select options that apply to you to get the most relevant plays.</div>
			</div>
		),
	},
	{
		target: '.evtable-row',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Get Bet Insights</h3>
				<div style={{ color: '#838693' }}>Click/tap on any bet to view line history graphs or compare odds across different sportsbooks</div>
			</div>
		),
	},
	{
		target: '.evtable-dropdown',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Customize your graphs</h3>
				<div style={{ color: '#838693' }}>Click on sportsbooks to add/remove them from the graph or modify the time horizon with the buttons above the graph.</div>
			</div>
		),
		// placement: 'auto',
		styles: {
			tooltip: {
				//   position: "absolute", // Force absolute positioning
				top: '-1200px', // Manually adjust the top position
				left: '400px', // Align the tooltip horizontally
				//   transform: "translateX(-50%)", // Center the tooltip
				//   zIndex: 9999, // Ensure it's above other elements
			},
		},
	},
	{
		target: '.discord-notification-button',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Discord notifications</h3>
				<div style={{ color: '#838693' }}>Connect your Discord account to receive play notifications as soon as opportunities are detected.</div>
			</div>
		),
		styles: {
			tooltip: {
				//   position: "absolute", // Force absolute positioning
				// top: '-1000px', // Manually adjust the top position
				// left: '400px', // Align the tooltip horizontally
				//   transform: "translateX(-50%)", // Center the tooltip
				zIndex: 9999, // Ensure it's above other elements
			},
		},
		placement: 'bottom',
		// disableBeacon: true,
	},
	{
		target: '.play-button',
		content: (
			<div style={{ backgroundColor: '#363844' }}>
				<h3 style={{ color: 'white', fontWeight: 'bold' }}>Run a scan</h3>
				<div style={{ color: '#838693' }}>Press the big blue button to get a fresh set of +EV plays!</div>
			</div>
		),
	},
];

export const EvScreen = (props) => {
	const { bankroll, setBankroll, tutorial, filtersOpen, setFiltersOpen, isMobile, onVideoClose } = props;
	const [isLoading, setIsLoading] = useState(false);
	const [isAutoRefresh, setIsAutoRefresh] = useState(false);
	const [isLive, setIsLive] = useState(false);
	const [data, setData] = useState(false);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	
	// Pagination state
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 50,
		total: 0,
		totalPages: 0,
		hasNext: false,
		hasPrev: false
	});
	// console.log('isFiltersOpen: ', isFiltersOpen, filtersOpen);
	const [user, setUser] = useState(JSON.parse(window.localStorage.getItem('userdb')));
	const [relatedOdds, setRelatedOdds] = useState();
	const [selectedRow, setSelectedRow] = useState();
	const [order, setOrder] = useState('desc');
	const [orderBy, setOrderBy] = useState('ev');
	const [preloadedRelatedOdds, setPreloadedRelatedOdds] = useState(new Map());
	const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
	const isRefreshingRef = useRef(false);
	const {  getAccessTokenSilently } = useAuth0();

	// Add effect for scroll locking
	useEffect(() => {
		if (isMobile && isFiltersOpen) {
			document.body.classList.add('scroll-lock');
		} else {
			document.body.classList.remove('scroll-lock');
		}

		// Cleanup function to ensure we remove the class when component unmounts
		return () => {
			document.body.classList.remove('scroll-lock');
		};
	}, [isMobile, isFiltersOpen]);

	const date = getFutureDate(user?.queries?.[0]?.[0]?.dateDifference || 7);

	// console.log(user?.queries.find((q) => q?.[0]?.name === user?.selected_query));

	const u_db = JSON.parse(window.localStorage.getItem('userdb'));
	const preferredSportsbooks = u_db.preferred_sportsbooks;

	let sportsbooks = [];
	if (preferredSportsbooks?.length > 0) {
		for (const bk of preferredSportsbooks) {
			sportsbooks.push({ label: bk, value: bk });
		}
	} else {
		sportsbooks = [{ label: 'bet365', value: 'bet365' }];
	}

	const [filters, setFilters] = useState(
		user?.queries?.find((q) => q?.[0]?.name === user.selected_query) ||
			(user?.queries?.[0]
				? user?.queries?.[0]
				: [
						{
							sports: user?.queries?.[0]?.[0]?.sports ? user?.queries?.[0]?.[0].sports : [],
							leagues: user?.queries?.[0]?.[0]?.leagues ? user?.queries?.[0]?.[0].leagues : [],
							sportsbooks: user?.queries?.[0]?.[0]?.sportsbooks ? user?.queries?.[0]?.[0].sportsbooks : [],
							sharpBooks: user?.queries?.[0]?.[0]?.sharpBooks
								? user?.queries?.[0]?.[0].sharpBooks
								: getWagerWizDisplayOptions(),
							markets: user?.queries?.[0]?.[0]?.markets ? user?.queries?.[0]?.[0].markets : [],
							marketTypes: user?.queries?.[0]?.[0]?.marketTypes ? user?.queries?.[0]?.[0].marketTypes : [],
							minMaxEv: user?.queries?.[0]?.[0]?.minMaxEv ? user?.queries?.[0]?.[0].minMaxEv : [1, 9999999],
							minMaxOdds: user?.queries?.[0]?.[0]?.minMaxOdds ? user?.queries?.[0]?.[0].minMaxOdds : [-9999999, 9999999],
							date: date ? date : new Date(),
						}
				  ])
	);

	useEffect(() => {
		if (!filters || filters.length === 0) {
			setFilters([
				{
					sports: user?.queries?.[0]?.[0]?.sports ? user?.queries?.[0]?.[0].sports : [],
					leagues: user?.queries?.[0]?.[0]?.leagues ? user?.queries?.[0]?.[0].leagues : [],
					sportsbooks: user?.queries?.[0]?.[0]?.sportsbooks ? user?.queries?.[0]?.[0].sportsbooks : [],
					sharpBooks: user?.queries?.[0]?.[0]?.sharpBooks
						? user?.queries?.[0]?.[0].sharpBooks
						: getWagerWizDisplayOptions(),
					markets: user?.queries?.[0]?.[0]?.markets ? user?.queries?.[0]?.[0].markets : [],
					marketTypes: user?.queries?.[0]?.[0]?.marketTypes ? user?.queries?.[0]?.[0].marketTypes : [],
					minMaxEv: user?.queries?.[0]?.[0]?.minMaxEv ? user?.queries?.[0]?.[0].minMaxEv : [1, 9999999],
					minMaxOdds: user?.queries?.[0]?.[0]?.minMaxOdds ? user?.queries?.[0]?.[0].minMaxOdds : [-9999999, 9999999],
					date: date ? date : new Date(),
				}
			]);
		}
	}, [filters, setFilters]);

	const [queries, setQueries] = useState(user?.queries);
	const [newQueryName, setNewQueryName] = useState('New query name');
	const newQueryNameRef = useRef();
	const renameQueryRef = useRef();
	const filtersRef = useRef(filters);
	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);
	const [isScannable, setIsScannable] = useState(false);

	// console.log('queries: ', queries);

	// console.log('filters: ', filters);

	const [isFilterSetOpen, setIsFilterSetOpen] = useState(false);
	const [isFilterSetModalOpen, setIsFilterSetModalOpen] = useState(false);

	// console.log('isFilterSetOpen: ', isFilterSetOpen);
	const [isFiltersSetAnchorEl, setIsFiltersSetAnchorEl] = useState();

	const [isPlacedUnplacedOpen, setIsPlacedUnplacedOpen] = useState(false);
	const [isPlacedAnchorEl, setIsPlacedAnchorEl] = useState();
	const [allBetsOpen, setAllBetsOpen] = useState('all');
	const [hasRunAlready, setHasRunAlready] = useState(false);
	// const [refreshKey, setRefreshKey] = useState(0);

	const [isScansLimitModalOpen, setIsScansLimitOpen] = useState(false);

	const executeEvScreenPaginated = async (page = 1, limit = 50) => {
		if (isDailyScanLimitReached()) {
			setIsScansLimitOpen(true);
			return;
		}

		setIsLoading(true);

		const filtersCopy = JSON.parse(JSON.stringify(filters));
		
		// Set default sharp books if none are selected
		for (const filter of filtersCopy) {
			if (!filter.sharpBooks.length) {
				filter.sharpBooks = getWagerWizDisplayOptions();
			}
			
			for (const sportsbook of filter.sharpBooks) {
				if (sportsbook.label === 'FanDuel') {
					sportsbook.label = 'Betway (Alaska)';
					sportsbook.value = 'Betway (Alaska)';
				}
			}
		}

		if (process.env.REACT_APP_EV_ENABLED !== 'false') {
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const fltrs = filtersCopy;
			const live = userdb?.live;

			const token = await getAccessTokenSilently();
			
			// Track accumulated data across all endpoints
			let accumulatedData = [];
			let scanLimitHit = false;
			
			// Helper function to process and merge new data
			const processAndMergeData = async (newData) => {
				console.log('[Price History] processAndMergeData called with', newData?.length || 0, 'rows');
				if (!newData || newData.length === 0) {
					console.log('[Price History] Early return - no data');
					return;
				}
				
				const d = JSON.parse(JSON.stringify(newData));
				
				const gameIds = [];
				const evDataIds = [];
				const evDataIdsSharp = [];
				const sports = [];

				for (const evdataRow of d) {
					// Determine which side (over/under) has better EV and construct outcome ID accordingly
					// Skip for Point Spread and Asian Handicap as they have different logic
					if (evdataRow.outcome_id_normalized && evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap') {
						const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
						if (splitoutcomeid.length >= 5) {
							// Determine which side to use based on EV
							const side = (evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 'under';
							const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
							// Reconstruct outcome ID with the correct side
							const outcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
							evDataIds.push(outcomeId);
						} else {
							// Fallback to original if format is unexpected
							evDataIds.push(evdataRow.outcome_id_normalized);
						}
					} else if (evdataRow.outcome_id_normalized) {
						// For Point Spread/Asian Handicap, use the outcome_id_normalized as-is
						evDataIds.push(evdataRow.outcome_id_normalized);
					}
					if (!sports.includes(evdataRow.sport)) {
						sports.push(evdataRow.sport);
					}

					if (!gameIds.some((gameId) => gameId === evdataRow.game_id)) {
						gameIds.push(evdataRow.game_id);
					}

					// Handle cases where sharp_book might be undefined or null
					if (evdataRow.sharp_book && evdataRow.outcome_id_normalized) {
						const sharpBook = evdataRow.sharp_book.split(',');
						const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
						if (splitoutcomeid.length >= 4) {
							// For sharp links, also use the correct side based on EV
							const side = (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 
							             (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over <= evdataRow.ev_under) ? 'under' : 
							             splitoutcomeid[4] || 'over';
							const sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
							evDataIdsSharp.push(sharpOutcomeId);
						}
					}
				}
				
				if (gameIds.length > 0) {
					const gamesData = await getGamesData(gameIds, token);
					const linksData = await getLinksData(evDataIds, sports);
					const sharpLinksData = await getLinksData(evDataIdsSharp, sports);

					for (const row of d) {
						// Construct the outcome ID with the correct side (over/under) based on EV
						let linkOutcomeId = row.outcome_id_normalized;
						if (row.outcome_id_normalized && row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
							const splitoutcomeid = row.outcome_id_normalized.split(':');
							if (splitoutcomeid.length >= 5) {
								const side = (row.ev_over > row.ev_under) ? 'over' : 'under';
								const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
								linkOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
							}
						}
						let link = linksData[linkOutcomeId] || null;
						
						// Handle cases where sharp_book might be undefined or null
						let sharpLink = null;
						let sharpOutcomeId = null;
						if (row.sharp_book && row.outcome_id_normalized) {
							const sharpBook = row.sharp_book.split(',');
							const splitoutcomeid = row.outcome_id_normalized.split(':');
							if (splitoutcomeid.length >= 4) {
								// Use the correct side based on EV
								const side = (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over > row.ev_under) ? 'over' : 
								             (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over <= row.ev_under) ? 'under' : 
								             splitoutcomeid[4] || 'over';
								sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
								sharpLink = sharpLinksData[sharpOutcomeId] || null;
							}
						}
						const game = gamesData.games.find((g) => g.game_id === row.game_id);

						row.link = link;
						row.sharpLink = sharpLink;
						row.game = game;
						// Store outcome IDs for lazy price history fetching by the table
						row._linkOutcomeId = linkOutcomeId;
						row._sharpOutcomeId = sharpOutcomeId;
						row._sport = row.sport;

						if (row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
							if (row.ev_over > row.ev_under) {
								row.ev = row.ev_over;
							} else {
								row.ev = row.ev_under;
							}
						}
					}
				}
				
				// Merge with accumulated data
				accumulatedData = [...accumulatedData, ...d];
				
				// Sort by EV (descending)
				accumulatedData.sort((a, b) => {
					const evA = a.ev || a.ev_over || 0;
					const evB = b.ev || b.ev_over || 0;
					return evB - evA;
				});
				
				// Update state immediately with accumulated data
				setData([...accumulatedData]);
				setPagination({
					page: page,
					limit: limit,
					total: accumulatedData.length,
					totalPages: Math.ceil(accumulatedData.length / limit),
					hasNext: false, // Will be updated when all requests complete
					hasPrev: page > 1
				});
				setHasRunAlready(true);
			};
			
			// Build queryRows for the API call (matching runEvScreenPaginated logic)
			const queryRows = [];
			for (const row of fltrs) {
				const queryRow = {};
				// Ensure dateDifference is set - calculate from date if missing
				if (row.dateDifference === undefined || row.dateDifference === null) {
					if (row.date) {
						const currentTime = Date.now();
						const fourHoursMs = 4 * 60 * 60 * 1000;
						const dateTime = new Date(row.date).getTime();
						const diffTime = dateTime - (currentTime - fourHoursMs);
						queryRow.dateDifference = Math.floor(diffTime / (1000 * 60 * 60 * 24));
					} else {
						// Default to 7 days if no date
						queryRow.dateDifference = 7;
					}
				} else {
					queryRow.dateDifference = row.dateDifference;
				}

				const currentTime = Date.now();
				const fourHoursMs = 4 * 60 * 60 * 1000;
				const futureTime = currentTime - fourHoursMs + queryRow.dateDifference * 24 * 60 * 60 * 1000;

				try {
					const futureDate = new Date(futureTime);
					queryRow.date = new Date(futureDate).toISOString();
				} catch (e) {
					const futureDate = new Date();
					queryRow.date = new Date(futureDate).toISOString();
				}

				queryRow.hide_alternates = row.hide_alternates;
				queryRow.limit = row.limit || '';
				queryRow.default = false;
				queryRow.name = 'temp';
				queryRow.id = 'temp';
				queryRow.league = [];
				queryRow.sport = [];
				queryRow.market = [];
				queryRow.marketType = [];
				queryRow.book = [];
				queryRow.sharp = [];
				queryRow.live = live;
				queryRow.minMaxOdds = row.minMaxOdds || [-9999999, 9999999];
				if (queryRow.minMaxOdds[0] === '' || queryRow.minMaxOdds[0] === null || queryRow.minMaxOdds[0] === undefined) {
					queryRow.minMaxOdds[0] = -9999999;
				}
				if (queryRow.minMaxOdds[1] === '' || queryRow.minMaxOdds[1] === null || queryRow.minMaxOdds[1] === undefined) {
					queryRow.minMaxOdds[1] = 9999999;
				}
				queryRow.minMaxEv = row.minMaxEv || [-9999999, 9999999];
				if (queryRow.minMaxEv[0] === '' || queryRow.minMaxEv[0] === null || queryRow.minMaxEv[0] === undefined) {
					queryRow.minMaxEv[0] = -9999999;
				}
				if (queryRow.minMaxEv[1] === '' || queryRow.minMaxEv[1] === null || queryRow.minMaxEv[1] === undefined) {
					queryRow.minMaxEv[1] = 9999999;
				}
				queryRow.minMaxEv = queryRow.minMaxEv.map((e) => Number(e));

				for (const league of row.leagues) {
					queryRow.league.push(league);
				}
				for (const sport of row.sports) {
					queryRow.sport.push(sport);
				}
				for (const mkt of row.markets) {
					queryRow.market.push(mkt);
				}
				for (const mkt of row.marketTypes) {
					queryRow.marketType.push(mkt);
				}
				// If no sportsbooks are selected, include all available sportsbooks
				const sportsbooksToProcess = row.sportsbooks && row.sportsbooks.length > 0 ? row.sportsbooks : DEFAULT_SPORTSBOOKS;
				
				for (const mkt of sportsbooksToProcess) {
					pushSportsbooksToQueryField(queryRow, 'book', mkt);
				}
				for (const mkt of row.sharpBooks) {
					pushSportsbooksToQueryField(queryRow, 'sharp', mkt);
				}

				queryRows.push(queryRow);
			}
			
			// Use progressive loading - onProgress is called as each endpoint responds
			const evrunres = await runEvScreenPaginatedProgressive(
				queryRows,
				user?.email,
				live,
				token,
				page,
				limit,
				async (endpoint, endpointData) => {
					// This callback is called immediately when each endpoint responds
					console.log(`[Price History] onProgress callback called for endpoint: ${endpoint}, data type: ${typeof endpointData}, isArray: ${Array.isArray(endpointData)}, length: ${Array.isArray(endpointData) ? endpointData.length : 'N/A'}`);
					
					if (endpointData === 'exceeded daily scans limit') {
						scanLimitHit = true;
						setIsScansLimitOpen(true);
						setIsLoading(false);
						return;
					}
					
					if (Array.isArray(endpointData) && endpointData.length > 0) {
						console.log(`[Price History] Calling processAndMergeData with ${endpointData.length} rows`);
						// Process and merge this endpoint's data immediately
						await processAndMergeData(endpointData);
					} else {
						console.log(`[Price History] Skipping processAndMergeData - endpointData is not a valid array or is empty`);
					}
				}
			);
			
			if (scanLimitHit) {
				return;
			}
			
			if (typeof evrunres === 'string') {
				setIsScansLimitOpen(true);
				setIsLoading(false);
			} else {
				incrementDailyScanCount();
				// Final update with complete pagination info
				setPagination(evrunres.pagination || {
					page: page,
					limit: limit,
					total: accumulatedData.length,
					totalPages: Math.ceil(accumulatedData.length / limit),
					hasNext: false,
					hasPrev: page > 1
				});
				setIsLoading(false);
			}
		} else {
			setData(parsedevScanResults);
			setPagination({
				page: 1,
				limit: 50,
				total: parsedevScanResults.length,
				totalPages: 1,
				hasNext: false,
				hasPrev: false
			});
			setHasRunAlready(true);
			setIsLoading(false);
		}
	};

	const executeEvScreen = async () => {
		if (isDailyScanLimitReached()) {
			setIsScansLimitOpen(true);
			return;
		}

		setIsLoading(true);

		const filtersCopy = JSON.parse(JSON.stringify(filters));
		
		// Set default sharp books if none are selected
		for (const filter of filtersCopy) {
			if (!filter.sharpBooks.length) {
				filter.sharpBooks = getWagerWizDisplayOptions();
			}
			
			for (const sportsbook of filter.sharpBooks) {
				if (sportsbook.label === 'FanDuel') {
					sportsbook.label = 'Betway (Alaska)';
					sportsbook.value = 'Betway (Alaska)';
				}
			}
		}

		if (process.env.REACT_APP_EV_ENABLED !== 'false') {
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			// Use current filters state instead of saved queries from localStorage
			const fltrs = filtersCopy; // Use the modified copy with default sharp books
			const live = userdb?.live;

			// Always proceed with the scan since we now ensure sharp books are set
			const token = await getAccessTokenSilently()
			const evrunres = await runEvScreen(user?.email, fltrs, live, token);
			
			if (typeof evrunres === 'string') {
				setIsScansLimitOpen(true);
				setIsLoading(false);
			} else {
				incrementDailyScanCount();
				// Handle both new paginated format and legacy format
				const { data: evData, pagination: paginationData } = evrunres;
				const d = JSON.parse(JSON.stringify(evData));

				const gameIds = [];
				const evDataIds = [];
				const evDataIdsSharp = [];
				const sports = [];

			for (const evdataRow of d) {
				// console.log('evdataRow: ', evdataRow);
				// Map outcome_id_normalized_no_book to outcome_id_normalized for backward compatibility
				if (evdataRow.outcome_id_normalized_no_book && !evdataRow.outcome_id_normalized) {
					evdataRow.outcome_id_normalized = evdataRow.outcome_id_normalized_no_book;
				}
				
				// Determine which side (over/under) has better EV and construct outcome ID accordingly
				// Skip for Point Spread and Asian Handicap as they have different logic
				if (evdataRow.outcome_id_normalized && evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap') {
					const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 5) {
						// Determine which side to use based on EV
						const side = (evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 'under';
						const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
						// Reconstruct outcome ID with the correct side
						const outcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
						evDataIds.push(outcomeId);
					} else {
						// Fallback to original if format is unexpected
						evDataIds.push(evdataRow.outcome_id_normalized);
					}
				} else if (evdataRow.outcome_id_normalized) {
					// For Point Spread/Asian Handicap, use the outcome_id_normalized as-is
					evDataIds.push(evdataRow.outcome_id_normalized);
				}
				
				if (!sports.includes(evdataRow.sport)) {
					sports.push(evdataRow.sport);
				}

				if (!gameIds.some((gameId) => gameId === evdataRow.game_id)) {
					gameIds.push(evdataRow.game_id);
				}

				// Handle cases where sharp_book might be undefined or null
				if (evdataRow.sharp_book && evdataRow.outcome_id_normalized) {
					const sharpBook = evdataRow.sharp_book.split(',');
					const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 4) {
						// For sharp links, also use the correct side based on EV
						const side = (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 
						             (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over <= evdataRow.ev_under) ? 'under' : 
						             splitoutcomeid[4] || 'over'; // Fallback to existing side or 'over'
						const sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
						evDataIdsSharp.push(sharpOutcomeId);
					}
				}
			}
				const token = await getAccessTokenSilently()
				const gamesData = await getGamesData(gameIds, token);
				const linksData = await getLinksData(evDataIds, sports);
				const sharpLinksData = await getLinksData(evDataIdsSharp, sports);

				for (const row of d) {
					// Construct the outcome ID with the correct side (over/under) based on EV
					let linkOutcomeId = row.outcome_id_normalized;
					if (row.outcome_id_normalized && row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
						const splitoutcomeid = row.outcome_id_normalized.split(':');
						if (splitoutcomeid.length >= 5) {
							const side = (row.ev_over > row.ev_under) ? 'over' : 'under';
							const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
							linkOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
						}
					}
					let link = linksData[linkOutcomeId] || null;

			// Handle cases where sharp_book might be undefined or null
			let sharpLink = null;
			let sharpOutcomeId = null;
			if (row.sharp_book && row.outcome_id_normalized) {
				const sharpBook = row.sharp_book.split(',');
				const splitoutcomeid = row.outcome_id_normalized.split(':');
				if (splitoutcomeid.length >= 4) {
					// Use the correct side based on EV
					const side = (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over > row.ev_under) ? 'over' : 
					             (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over <= row.ev_under) ? 'under' : 
					             splitoutcomeid[4] || 'over';
					sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
					sharpLink = sharpLinksData[sharpOutcomeId] || null;
				}
			}
					const game = gamesData.games.find((g) => g.game_id === row.game_id);

					row.link = link;
					row.sharpLink = sharpLink;
					row.game = game;
					// Store outcome IDs for lazy price history fetching by the table
					row._linkOutcomeId = linkOutcomeId;
					row._sharpOutcomeId = sharpOutcomeId;
					row._sport = row.sport;

					if (row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
						if (row.ev_over > row.ev_under) {
							row.ev = row.ev_over;
						} else {
							row.ev = row.ev_under;
						}
					}
				}

				setData(d);
				setPagination(paginationData);
				setHasRunAlready(true);
				setIsLoading(false);
			}
		} else {
			setData(parsedevScanResults);
			setPagination({
				page: 1,
				limit: 50,
				total: parsedevScanResults.length,
				totalPages: 1,
				hasNext: false,
				hasPrev: false
			});
			setHasRunAlready(true);
			setIsLoading(false);
		}
	};

	// Pagination handlers (client-side: data is already fully loaded, just slice it)
	const handlePageChange = (newPage) => {
		setPagination((prev) => {
			const total = prev.total || 0;
			const limit = prev.limit || 50;
			const totalPages = Math.max(1, Math.ceil(total / limit));
			const clampedPage = Math.min(Math.max(1, newPage), totalPages);
			return {
				...prev,
				page: clampedPage,
				totalPages,
				hasNext: clampedPage < totalPages,
				hasPrev: clampedPage > 1,
			};
		});
		// Scroll the table back to the top when changing pages
		try {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} catch (e) {}
	};

	const handleLimitChange = (newLimit) => {
		setPagination((prev) => {
			const total = prev.total || 0;
			const limit = Number(newLimit) || 50;
			const totalPages = Math.max(1, Math.ceil(total / limit));
			return {
				...prev,
				page: 1,
				limit,
				totalPages,
				hasNext: totalPages > 1,
				hasPrev: false,
			};
		});
	};

	// Keep pagination metadata (total / totalPages / hasNext / hasPrev) in sync with
	// the currently loaded data. The backend returns the full result set in one
	// request, so we paginate client-side here.
	useEffect(() => {
		const total = Array.isArray(data) ? data.length : 0;
		setPagination((prev) => {
			const limit = prev?.limit || 50;
			const totalPages = Math.max(1, Math.ceil(total / limit));
			const page = Math.min(Math.max(1, prev?.page || 1), totalPages);
			return {
				...prev,
				page,
				limit,
				total,
				totalPages,
				hasNext: page < totalPages,
				hasPrev: page > 1,
			};
		});
	}, [data]);

	// The slice of the full dataset that corresponds to the current page.
	const pagedData = useMemo(() => {
		if (!Array.isArray(data) || data.length === 0) return data;
		const limit = pagination?.limit || 50;
		const page = pagination?.page || 1;
		const start = (page - 1) * limit;
		const end = start + limit;
		return data.slice(start, end);
	}, [data, pagination?.page, pagination?.limit]);

	// useEffect(() => {
	// 	if (hasRunAlready) {
	// 		executeEvScreen()
	// 	}
	// }, [filters])

	useEffect(() => {
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		if (userdb) {
			userdb.live = isLive;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
		}

		const evScreenRun = async () => {
			await executeEvScreen();
		};
		if (hasRunAlready) {
			evScreenRun();
		}
	}, [isLive]);
	
	// Related odds are now lazy-loaded in evScreenTable.js based on viewport visibility
	// No bulk preloading needed - only visible rows will fetch related odds

	useEffect(() => {
		const getUser = async () => {
			let u = JSON.parse(window.localStorage.getItem('userdb'));
			let count = 0;
			while (!u && count < 50) {
				u = JSON.parse(window.localStorage.getItem('userdb'));
				// console.log(JSON.parse(window.localStorage.getItem('userdb')));
				count += 1;
			}
			setUser(u);
			setFilters(
				u?.queries?.find((q) => q?.[0]?.name === u.selected_query) ||
					(u?.queries?.[0]
						? u.queries?.[0]
						: [
								{
									sports: u?.queries?.[0]?.[0].sports ? u?.queries?.[0]?.[0].sports : [],
									leagues: u?.queries?.[0]?.[0].leagues ? u?.queries?.[0]?.[0].leagues : [],
									sportsbooks: u?.queries?.[0]?.[0].sportsbooks ? u?.queries?.[0]?.[0].sportsbooks : [{ label: 'bet365', value: 'bet365' }],
									sharpBooks: u?.queries?.[0]?.[0].sharpBooks
										? u?.queries?.[0]?.[0].sharpBooks
										: getWagerWizDisplayOptions(),
									markets: u?.queries?.[0]?.[0].markets ? u?.queries?.[0]?.[0].markets : [],
									marketTypes: u?.queries?.[0]?.[0].marketTypes ? u?.queries?.[0]?.[0].marketTypes : [{ label: 'Main Markets', value: 'Main Markets' }],
									minMaxEv: u?.queries?.[0]?.[0].minMaxEv ? u?.queries?.[0]?.[0].minMaxEv : [1, 9999999],
									minMaxOdds: u?.queries?.[0]?.[0].minMaxOdds ? u?.queries?.[0]?.[0].minMaxOdds : [-9999999, 9999999],
									date: date ? date : new Date(),
								},
								{
									sports: u?.queries?.[0]?.[0].sports ? u?.queries?.[0]?.[0].sports : [],
									leagues: u?.queries?.[0]?.[0].leagues ? u?.queries?.[0]?.[0].leagues : [],
									sportsbooks: u?.queries?.[0]?.[0].sportsbooks ? u?.queries?.[0]?.[0].sportsbooks : [{ label: 'bet365', value: 'bet365' }],
									sharpBooks: u?.queries?.[0]?.[0].sharpBooks
										? u?.queries?.[0]?.[0].sharpBooks
										: getWagerWizDisplayOptions(),
									markets: u?.queries?.[0]?.[0].markets ? u?.queries?.[0]?.[0].markets : [],
									marketTypes: u?.queries?.[0]?.[0].marketTypes ? u?.queries?.[0]?.[0].marketTypes : [{ label: 'Player Props', value: 'Player Props' }],
									minMaxEv: u?.queries?.[0]?.[0].minMaxEv ? u?.queries?.[0]?.[0].minMaxEv : [3, 9999999],
									minMaxOdds: u?.queries?.[0]?.[0].minMaxOdds ? u?.queries?.[0]?.[0].minMaxOdds : [-9999999, 9999999],
									date: date ? date : new Date(),
								},
						  ])
			);
			setQueries(u?.queries);

			if (!tutorial) {
				// only autoload if it's not the tutorial
				await delay(400);
				await executeEvScreen();
			}
		};

		getUser();
	}, []);

	const getAutoRefresh = async () => {
		try {
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			// Prefer live filters state so user customizations are honored; fall back to saved query.
			const fltrsRaw = (filtersRef.current && filtersRef.current.length > 0)
				? filtersRef.current
				: (userdb?.queries?.find((q) => q?.[0]?.name === userdb?.selected_query) || userdb?.queries?.[0]);

			if (!fltrsRaw || fltrsRaw.length === 0) {
				console.warn('[AutoRefresh] skipping tick: no filters available');
				return;
			}

			// Mirror executeEvScreen: deep-copy and ensure sharp books are populated.
			// Without this, an empty sharpBooks array causes the backend to return no results.
			const fltrs = JSON.parse(JSON.stringify(fltrsRaw));
			for (const filter of fltrs) {
				if (!filter.sharpBooks || !filter.sharpBooks.length) {
					filter.sharpBooks = getWagerWizDisplayOptions();
				}
				for (const sportsbook of filter.sharpBooks) {
					if (sportsbook.label === 'FanDuel') {
						sportsbook.label = 'Betway (Alaska)';
						sportsbook.value = 'Betway (Alaska)';
					}
				}
			}

			const live = userdb?.live;
			const token = await getAccessTokenSilently();
			const evrunres = await runEvScreen(userdb?.email, fltrs, live, token);

			if (!evrunres) {
				console.warn('[AutoRefresh] request failed; leaving last data in place');
				return;
			}
			if (typeof evrunres === 'string') {
				console.warn('[AutoRefresh] backend returned string (likely scan limit):', evrunres);
				// Still mark as "refreshed" so UI reflects that the tick completed.
				setLastRefreshedAt(Date.now());
				return;
			}

			const evData = evrunres?.data;
			const paginationData = evrunres?.pagination;
			if (!Array.isArray(evData) || evData.length === 0) {
				console.log('[AutoRefresh] tick completed with 0 rows');
				setLastRefreshedAt(Date.now());
				return;
			}

			const d = JSON.parse(JSON.stringify(evData));

			const gameIds = [];
			const evDataIds = [];
			const evDataIdsSharp = [];
			const sports = [];

			for (const evdataRow of d) {
				if (evdataRow.outcome_id_normalized_no_book && !evdataRow.outcome_id_normalized) {
					evdataRow.outcome_id_normalized = evdataRow.outcome_id_normalized_no_book;
				}

				if (evdataRow.outcome_id_normalized && evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap') {
					const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 5) {
						const side = (evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 'under';
						const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
						const outcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
						evDataIds.push(outcomeId);
					} else {
						evDataIds.push(evdataRow.outcome_id_normalized);
					}
				} else if (evdataRow.outcome_id_normalized) {
					evDataIds.push(evdataRow.outcome_id_normalized);
				}

				if (evdataRow.sport && !sports.includes(evdataRow.sport)) {
					sports.push(evdataRow.sport);
				}
				if (evdataRow.game_id && !gameIds.includes(evdataRow.game_id)) {
					gameIds.push(evdataRow.game_id);
				}

				if (evdataRow.sharp_book && evdataRow.outcome_id_normalized) {
					const sharpBook = evdataRow.sharp_book.split(',');
					const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 4) {
						const side = (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over > evdataRow.ev_under) ? 'over' :
							(evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over <= evdataRow.ev_under) ? 'under' :
							(splitoutcomeid[4] || 'over');
						const sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
						evDataIdsSharp.push(sharpOutcomeId);
					}
				}
			}

			if (gameIds.length > 0) {
				const gamesData = await getGamesData(gameIds, token);
				const linksData = await getLinksData(evDataIds, sports);
				const sharpLinksData = await getLinksData(evDataIdsSharp, sports);

				for (const row of d) {
					let linkOutcomeId = row.outcome_id_normalized;
					if (row.outcome_id_normalized && row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
						const splitoutcomeid = row.outcome_id_normalized.split(':');
						if (splitoutcomeid.length >= 5) {
							const side = (row.ev_over > row.ev_under) ? 'over' : 'under';
							const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
							linkOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
						}
					}
					const link = (linksData && linksData[linkOutcomeId]) || null;

					let sharpLink = null;
					let sharpOutcomeId = null;
					if (row.sharp_book && row.outcome_id_normalized) {
						const sharpBook = row.sharp_book.split(',');
						const splitoutcomeid = row.outcome_id_normalized.split(':');
						if (splitoutcomeid.length >= 4) {
							const side = (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over > row.ev_under) ? 'over' :
								(row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over <= row.ev_under) ? 'under' :
								(splitoutcomeid[4] || 'over');
							sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
							sharpLink = (sharpLinksData && sharpLinksData[sharpOutcomeId]) || null;
						}
					}
					const game = gamesData?.games?.find((g) => g.game_id === row.game_id);

					row.link = link;
					row.sharpLink = sharpLink;
					row.game = game;
					row._linkOutcomeId = linkOutcomeId;
					row._sharpOutcomeId = sharpOutcomeId;
					row._sport = row.sport;

					if (row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
						if (row.ev_over > row.ev_under) {
							row.ev = row.ev_over;
						} else {
							row.ev = row.ev_under;
						}
					}
				}
			}

			setData(d);
			if (paginationData) {
				setPagination((prev) => ({
					...prev,
					total: paginationData.total ?? d.length,
					totalPages: paginationData.totalPages ?? Math.ceil(d.length / (prev?.limit || 50)),
				}));
			}
			setLastRefreshedAt(Date.now());
			console.log('[AutoRefresh] tick completed with', d.length, 'rows');
		} catch (e) {
			console.error('[AutoRefresh] error:', e);
			// Still mark the tick as completed so the indicator doesn't sit on "Refreshing…" forever.
			setLastRefreshedAt(Date.now());
		}
	};

	useEffect(() => {
		if (!isAutoRefresh) {
			setLastRefreshedAt(null);
			return;
		}

		const tick = async () => {
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
			if (isRefreshingRef.current) return;
			isRefreshingRef.current = true;
			try {
				await getAutoRefresh();
			} finally {
				isRefreshingRef.current = false;
			}
		};

		tick();
		const intervalId = setInterval(tick, 8000);
		const onVis = () => {
			if (document.visibilityState === 'visible') tick();
		};
		document.addEventListener('visibilitychange', onVis);

		return () => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVis);
		};
	}, [isAutoRefresh]);

	useEffect(() => {
		// Always allow scanning - no need to require sportsbooks to be selected
		setIsScannable(true);
	}, [filters, isScannable, setIsScannable]);

	const [isVideoModalOpen, setIsVideoModalOpen] = useState(tutorial);
	const hasFiredVideoCloseRef = useRef(false);

	useEffect(() => {
		if (tutorial && !isVideoModalOpen && !hasFiredVideoCloseRef.current) {
			hasFiredVideoCloseRef.current = true;
			if (onVideoClose) onVideoClose();
		}
	}, [tutorial, isVideoModalOpen, onVideoClose]);

	return (
		<div className="page flex flex-col" style={{ maxWidth: '100vw', overflowX: 'hidden', paddingLeft: isMobile ? '8px' : '8px', paddingRight: isMobile ? '8px' : '8px' }}>
			<Modal open={isVideoModalOpen} onClose={() => setIsVideoModalOpen(false)}>
				<div
					style={{
						position: 'relative',
						justifySelf: 'center',
						border: '1px solid #23262F',
						borderRadius: '10px',
						backgroundColor: '#22262e',
						marginTop: '12vh',
						width: '80%',
						outline: 'none',
					}}>
					{/* Close Button */}
					<button
						onClick={() => setIsVideoModalOpen(false)}
						style={{
							position: 'absolute',
							top: 10,
							right: 10,
							background: 'transparent',
							border: 'none',
							color: 'white',
							fontSize: '1.5rem',
							cursor: 'pointer',
						}}
						aria-label="Close">
						&times;
					</button>

					<div style={{ textAlign: 'center', padding: 10, marginRight: 20, marginLeft: 20, marginTop: 20 }}>
						<VimeoEmbed videoId="1075849944?h=717c54798f" />
					</div>
				</div>
			</Modal>
			<UpgradeModal message={'Scans limit exceeded for your pricing plan'} isOpen={isScansLimitModalOpen} setIsOpen={() => setIsScansLimitOpen(false)} />
			<div style={{ flex: 1 }}>
				<div className="px-4 md:px-4 xl:px-6 mb-6">
					<div className="w-full max-w-[95vw] mx-auto">
						<h2 style={{ textAlign: isMobile ? 'center' : 'left' }} className="text-3xl ev-scanner-title">
							EV Scanner
						</h2>
					</div>
				</div>
				{!isMobile && (
					<div className="px-4 md:px-4 xl:px-6">
						<EvScreenControlButtons
						isMobile={isMobile}
						isScannable={isScannable}
						executeEvScreen={executeEvScreen}
						play_icon={play_icon}
						setIsAutoRefresh={setIsAutoRefresh}
						isAutoRefresh={isAutoRefresh}
						lastRefreshedAt={lastRefreshedAt}
						runEvScreen={runEvScreen}
						filters={filters}
						isLive={isLive}
						getGamesData={getGamesData}
						getLinksData={getLinksData}
						setData={setData}
						setIsFiltersOpen={setIsFiltersOpen}
						setIsFiltersSetAnchorEl={setIsFiltersSetAnchorEl}
						setIsLive={setIsLive}
						setIsFilterSetOpen={setIsFilterSetOpen}
						isFilterSetOpen={isFilterSetOpen}
						isFilterSetModalOpen={isFilterSetModalOpen}
						setIsFilterSetModalOpen={setIsFilterSetModalOpen}
						user={user}
						setUser={setUser}
						eye_icon={eye_icon}
						isFiltersSetAnchorEl={isFiltersSetAnchorEl}
						queries={queries}
						setQueries={setQueries}
						setFilters={setFilters}
						newQueryName={newQueryName}
						setNewQueryName={setNewQueryName}
						newQueryNameRef={newQueryNameRef}
						edit_icon={edit_icon}
						setFiltersOpen={setFiltersOpen}
						filtersOpen={filtersOpen}
						setIsPlacedUnplacedOpen={setIsPlacedUnplacedOpen}
						setIsPlacedAnchorEl={setIsPlacedAnchorEl}
						isPlacedUnplacedOpen={isPlacedUnplacedOpen}
						lightbulb_icon={lightbulb_icon}
						allBetsOpen={allBetsOpen}
						setAllBetsOpen={setAllBetsOpen}
						isPlacedAnchorEl={isPlacedAnchorEl}
						isFiltersOpen={isFiltersOpen}
						filter_icon={filter_icon}
					/>
					</div>
				)}
				
				
				{isMobile && (
					<EvScreenControlButtonsMobile
						isMobile={isMobile}
						isScannable={isScannable}
						executeEvScreen={executeEvScreen}
						play_icon={play_icon}
						setIsAutoRefresh={setIsAutoRefresh}
						isAutoRefresh={isAutoRefresh}
						lastRefreshedAt={lastRefreshedAt}
						runEvScreen={runEvScreen}
						filters={filters}
						isLive={isLive}
						getGamesData={getGamesData}
						getLinksData={getLinksData}
						setData={setData}
						setIsFiltersOpen={setIsFiltersOpen}
						setIsFiltersSetAnchorEl={setIsFiltersSetAnchorEl}
						setIsLive={setIsLive}
						setIsFilterSetOpen={setIsFilterSetOpen}
						isFilterSetOpen={isFilterSetOpen}
						isFilterSetModalOpen={isFilterSetModalOpen}
						setIsFilterSetModalOpen={setIsFilterSetModalOpen}
						user={user}
						setUser={setUser}
						eye_icon={eye_icon}
						isFiltersSetAnchorEl={isFiltersSetAnchorEl}
						queries={queries}
						setQueries={setQueries}
						setFilters={setFilters}
						newQueryName={newQueryName}
						setNewQueryName={setNewQueryName}
						newQueryNameRef={newQueryNameRef}
						edit_icon={edit_icon}
						setFiltersOpen={setFiltersOpen}
						filtersOpen={filtersOpen}
						setIsPlacedUnplacedOpen={setIsPlacedUnplacedOpen}
						setIsPlacedAnchorEl={setIsPlacedAnchorEl}
						isPlacedUnplacedOpen={isPlacedUnplacedOpen}
						lightbulb_icon={lightbulb_icon}
						allBetsOpen={allBetsOpen}
						setAllBetsOpen={setAllBetsOpen}
						isPlacedAnchorEl={isPlacedAnchorEl}
						isFiltersOpen={isFiltersOpen}
						filter_icon={filter_icon}
						order={order}
						setOrder={setOrder}
						orderBy={orderBy}
						setOrderBy={setOrderBy}
					/>
				)}

				{filtersOpen && (
					<div className="px-4 mb-6">
						<div className="w-full max-w-[95vw] mx-auto">
							<FiltersContainer
								filters={filters}
								setFilters={setFilters}
								setIsFiltersOpen={setIsFiltersOpen}
								filtersOpen={filtersOpen}
								email={user?.email}
								isMobile={isMobile}
								setFiltersOpen={setFiltersOpen}
								isScannable={isScannable}
								executeEvScreen={executeEvScreen}
								setIsFiltersSetAnchorEl={setIsFiltersSetAnchorEl}
								setIsFilterSetOpen={setIsFilterSetOpen}
								isFilterSetOpen={isFilterSetOpen}
								isFiltersSetAnchorEl={isFiltersSetAnchorEl}
								setUser={setUser}
								setIsFilterSetModalOpen={setIsFilterSetModalOpen}
								isFilterSetModalOpen={isFilterSetModalOpen}
								userDb={user}
								setNewQueryName={setNewQueryName}
								newQueryNameRef={newQueryNameRef}
								queries={queries}
								setQueries={setQueries}
							/>
						</div>
					</div>
				)}

				<div className="px-4 md:px-4 xl:px-6 pt-8">
					<div className="w-full max-w-[95vw] mx-auto">
						{/* Pagination Controls - positioned right above table */}
						{data && data.length > 0 && (
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
								{isMobile && process.env.REACT_APP_CONFIDENCE !== 'false' && (
									<span style={{ fontSize: '0.7rem', color: '#9ca3af', marginRight: 'auto' }}>
										* Confidence score is in beta. Always verify your own plays and exercise caution.
									</span>
								)}
								<CompactPaginationControls
									pagination={pagination}
									onPageChange={handlePageChange}
									onLimitChange={handleLimitChange}
									isLoading={isLoading}
								/>
							</div>
						)}
						
						<EVScreenTable
							data={pagedData}
							isLoading={isLoading}
							bankroll={bankroll}
							relatedOdds={relatedOdds}
							setRelatedOdds={setRelatedOdds}
							preloadedRelatedOdds={preloadedRelatedOdds}
							selectedRow={selectedRow}
							setSelectedRow={setSelectedRow}
							allBetsOpen={allBetsOpen}
							tutorial={tutorial}
							isLive={isLive}
							isAutoRefresh={isAutoRefresh}
							loadingIcon={wwlogo_gif}
							isMobileProp={isMobile}
							order={order}
							setOrder={setOrder}
							orderBy={orderBy}
							setOrderBy={setOrderBy}
							sharpFilter={filters?.[0]?.sharpBooks?.[0]?.value || 'WagerWiz'}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
